import dayjs from "dayjs";
import "dayjs/locale/zh-cn";
import "dayjs/locale/en";

/**
 * 根据用户地区获取对应的 locale
 * 优先级：用户信息中的国家/地区 > localStorage > 浏览器语言设置
 */
export const getUserLocale = () => {
    // 尝试从用户信息中获取国家/地区信息
    try {
        const userInfoStr = localStorage.getItem("uvita-userInfo");
        if (userInfoStr) {
            const userInfo = JSON.parse(userInfoStr);
            // 如果用户信息中有国家或地区字段，使用它
            const country = userInfo?.country || userInfo?.locale || userInfo?.user?.country || userInfo?.user?.locale;
            if (country) {
                // 如果国家代码是单独的国家代码（如 "US", "CN", "CA"），需要转换为 locale 格式
                if (country.length === 2) {
                    // 简单映射：US -> en-US, CN -> zh-CN, CA -> en-CA
                    const countryToLocaleMap = {
                        US: "en-US",
                        CN: "zh-CN",
                        CA: "en-CA",
                        GB: "en-GB",
                        FR: "fr-FR",
                        DE: "de-DE",
                        JP: "ja-JP",
                        KR: "ko-KR",
                    };
                    const locale = countryToLocaleMap[country.toUpperCase()];
                    if (locale) return locale;
                } else {
                    // 如果已经是 locale 格式（如 "zh-CN"），直接返回
                    return country;
                }
            }
        }
    } catch (e) {
        // 忽略解析错误
    }

    // 从 localStorage 获取用户设置的语言/地区
    const storedLocale = localStorage.getItem("locale") || localStorage.getItem("language");
    if (storedLocale) return storedLocale;

    // 从浏览器语言设置获取
    const browserLocale = navigator.language || navigator.userLanguage || "en-US";

    return browserLocale;
};

/**
 * 根据国家/地区代码映射到对应的 dayjs locale
 */
export const getDayjsLocale = (localeCode) => {
    // 提取语言代码（例如：zh-CN -> zh, en-US -> en）
    const language = localeCode?.split("-")[0]?.toLowerCase() || "en";

    // 特殊处理：中文
    if (language === "zh") {
        return "zh-cn";
    }

    // 默认返回英文
    return "en";
};

/**
 * 根据地区获取日期格式字符串
 * @param {string} localeCode - 地区代码，如 'zh-CN', 'en-US', 'en-CA'
 * @returns {string} dayjs 格式字符串
 */
export const getDateFormatByLocale = (localeCode) => {
    if (!localeCode) {
        localeCode = getUserLocale();
    }

    const country = localeCode?.split("-")[1]?.toUpperCase();

    // 中国：YYYY-MM-DD HH:mm
    if (localeCode.startsWith("zh")) {
        return "YYYY-MM-DD HH:mm";
    }

    // 美国：M/D/YYYY, h:mm a
    if (country === "US") {
        return "M/D/YYYY, h:mm a";
    }

    // 加拿大：通常使用 M/D/YYYY 格式（英语区）或 D/M/YYYY（法语区）
    if (country === "CA") {
        // 可以根据语言判断：如果是法语（fr-CA）使用 D/M/YYYY，否则使用 M/D/YYYY
        if (localeCode.startsWith("fr")) {
            return "D/M/YYYY, H:mm";
        }
        return "M/D/YYYY, h:mm a";
    }

    // 默认使用美国格式
    return "M/D/YYYY, h:mm a";
};

/**
 * 格式化日期，根据用户地区自动选择格式
 * @param {Date|string|number} date - 要格式化的日期
 * @param {string} customLocale - 可选的自定义地区代码
 * @returns {string} 格式化后的日期字符串
 */
export const formatDateByLocale = (date, customLocale = null) => {
    if (!date) return "-";

    // Guard against plain objects (e.g. a Dayjs instance serialized via structured clone loses its methods).
    // Only accept types that dayjs can safely parse.
    const isValidInput =
        typeof date === "string" ||
        typeof date === "number" ||
        date instanceof Date;
    if (!isValidInput) return "-";

    const locale = customLocale || getUserLocale();
    const dayjsLocale = getDayjsLocale(locale);
    const format = getDateFormatByLocale(locale);

    // 设置 dayjs 的 locale
    dayjs.locale(dayjsLocale);

    const parsed = dayjs(date);
    if (!parsed.isValid()) return "-";

    return parsed.format(format);
};

/**
 * 使用 Intl.DateTimeFormat 格式化日期（更符合本地化标准）
 * @param {Date|string|number} date - 要格式化的日期
 * @param {string} customLocale - 可选的自定义地区代码
 * @returns {string} 格式化后的日期字符串
 */
export const formatDateIntl = (date, customLocale = null) => {
    if (!date) return "-";

    const locale = customLocale || getUserLocale();
    const dateObj = new Date(date);

    const options = {
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
    };

    return new Intl.DateTimeFormat(locale, options).format(dateObj);
};

